import { ReactNode } from 'react';
import { Box, Stack, Typography } from '@rossum/ui/material';
import { useComponentsContext } from '../ComponentsContext';
import { TileChip } from './TileChip';
import { TileChip as TileChipType, TileProps } from './types';

type TileContentProps = {
  title: ReactNode;
  description?: ReactNode;
  inlineBadges?: TileChipType[];
  chips?: TileChipType[];
  to?: TileProps['to'];
  disabled?: boolean;
};

export const TileContent = ({
  title,
  description,
  inlineBadges,
  chips,
  to,
  disabled,
}: TileContentProps) => {
  const { LinkComponent } = useComponentsContext();
  const content = (
    <Stack sx={{ minWidth: 0, gap: 0.5 }}>
      {/* Title row with inline badges */}
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
        {typeof title === 'string' ? (
          <Typography
            variant="h6"
            sx={{
              minWidth: 0,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
          >
            {title}
          </Typography>
        ) : (
          title
        )}
        {inlineBadges?.map(chip => (
          <span key={chip.key} onClick={e => e.stopPropagation()}>
            <TileChip chip={chip} />
          </span>
        ))}
      </Stack>

      {/* Description */}
      {description && (
        <>
          {typeof description === 'string' ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                minWidth: 0,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              {description}
            </Typography>
          ) : (
            description
          )}
        </>
      )}

      {/* Chips below content */}
      {chips && chips.length > 0 && (
        <Stack
          direction="row"
          spacing={0.5}
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 0.5 }}
        >
          {chips.map(chip => (
            <TileChip key={chip.key} chip={chip} />
          ))}
        </Stack>
      )}
    </Stack>
  );

  // If there's a navigation target, wrap content in a link
  if (to) {
    return (
      <Box
        component={LinkComponent}
        to={to}
        tabIndex={-1}
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          color: t => t.palette.text[disabled ? 'secondary' : 'primary'],
          textDecoration: 'none',
          cursor: 'pointer',
          '&:hover': {
            color: t => t.palette.text.primary,
            textDecoration: 'none',
          },
        }}
      >
        {content}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        color: t => t.palette.text[disabled ? 'secondary' : 'primary'],
      }}
    >
      {content}
    </Box>
  );
};
